import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, Check, X, Eye } from "lucide-react";
import styles from "./TeamLogos.module.css";

const DEFAULT_TEAM_LOGO = "https://cdn.topsekk.com/file/default-team-logo.png";

const TeamLogoCard = ({ team, onUpload, index }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    setUploadProgress(0);
    try {
      await onUpload(team.teamId, file, (progress) => {
        setUploadProgress(progress);
      });
      setPreview(null);
      setUploadProgress(0);
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadProgress(0);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.05,
        duration: 0.3,
      },
    },
  };

  return (
    <>
      <motion.div
        className={`${styles.teamLogoCard} ${!team.hasLogo ? styles.noLogo : ""}`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover={{ scale: 1.02 }}
      >
        <div className={styles.cardHeader}>
          <div className={styles.teamInfo}>
            <h3 className={styles.teamName}>{team.teamName}</h3>
            <div className={styles.teamMeta}>
              <span className={styles.teamCode}>{team.code}</span>
              <span className={styles.teamCompetition}>{team.competitionName}</span>
            </div>
          </div>
          {team.hasLogo ? (
            <span className={`${styles.statusBadge} ${styles.success}`}>
              <Check size={14} />
              Has Logo
            </span>
          ) : (
            <span className={`${styles.statusBadge} ${styles.warning}`}>
              <X size={14} />
              No Logo
            </span>
          )}
        </div>

        <div className={styles.logoContainer}>
          {preview ? (
            <div className={styles.previewWrapper}>
              <img src={preview} alt="Preview" className={styles.logoPreview} />
              <div className={styles.previewOverlay}>
                <span className={styles.uploadingText}>
                  Uploading... {uploadProgress > 0 ? `${uploadProgress}%` : ''}
                </span>
              </div>
            </div>
          ) : (
            <div className={styles.logoWrapper} onClick={() => setShowPreviewModal(true)}>
              <img
                src={team.logoUrl || DEFAULT_TEAM_LOGO}
                alt={team.teamName}
                className={styles.teamLogoImage}
                onError={(e) => {
                  if (e.target.src !== DEFAULT_TEAM_LOGO) {
                    e.target.src = DEFAULT_TEAM_LOGO;
                    e.target.onerror = null;
                  }
                }}
              />
              <div className={styles.logoOverlay}>
                <Eye size={24} />
              </div>
            </div>
          )}
        </div>

        <div className={styles.cardDetails}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>ID:</span>
            <span className={styles.detailValue}>{team.teamId}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Players:</span>
            <span className={styles.detailValue}>{team.totalPlayers || 0}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Updated:</span>
            <span className={styles.detailValue}>{formatDate(team.logoUpdatedAt)}</span>
          </div>
        </div>

        <div className={styles.cardActions}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileSelect}
            accept="image/*"
            disabled={uploading}
          />
          <button
            className={`${styles.uploadBtn} ${uploading ? styles.uploading : ""}`}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <span className={styles.spinner}></span>
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} />
                {team.hasLogo ? "Replace Logo" : "Upload Logo"}
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className={styles.previewModal} onClick={() => setShowPreviewModal(false)}>
          <div className={styles.previewModalContent} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.modalClose}
              onClick={() => setShowPreviewModal(false)}
            >
              <X size={24} />
            </button>
            <img
              src={team.logoUrl || DEFAULT_TEAM_LOGO}
              alt={team.teamName}
              className={styles.modalImage}
              onError={(e) => {
                if (e.target.src !== DEFAULT_TEAM_LOGO) {
                  e.target.src = DEFAULT_TEAM_LOGO;
                  e.target.onerror = null;
                }
              }}
            />
            <div className={styles.modalInfo}>
              <h3>{team.teamName}</h3>
              <p>{team.officialName}</p>
              <p className={styles.modalMeta}>
                {team.code} • {team.competitionName}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeamLogoCard;